/**
 * Login page with OAuth redirect.
 *
 * Displays a centered login card with "Sign in with Authentik" button.
 * After clicking, user is redirected to Authentik for OIDC authentication.
 * After authentication, user is redirected back to the app via /auth/callback.
 */

import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/useAuth';
import { Sparkles } from 'lucide-react';
import { getBrand } from '../config/brand';
import { useTenantPageTitle } from '../hooks/useTenantPageTitle';

export function LoginPage() {
  const brand = getBrand();
  // Evenstar SSO-login branding (2026-08-04): the workspace's mount of this
  // hook lives under ProtectedRoute and never runs here, so the static
  // "GameMagick AI" tab title survived the whole unauthenticated login view.
  // Mounting it here covers /login + /auth/callback (pre-tenant branch).
  useTenantPageTitle();
  const { isAuthenticated, isLoading, login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Get the page they were trying to access (from ProtectedRoute redirect)
  const from = (location.state as { from?: Location })?.from?.pathname || '/';

  // If already authenticated, redirect to intended page
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, from]);

  const handleLogin = () => {
    login(from);
  };

  // Show loading while checking existing auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-vibe-bg">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-vibe-accent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-vibe-bg">
      <div className="max-w-md w-full space-y-8 p-8 bg-vibe-bg-secondary rounded-xl shadow-lg border border-vibe-border">
        {/* Logo and Title */}
        <div className="text-center">
          <div className="mx-auto w-16 h-16 rounded-xl bg-vibe-accent/20 flex items-center justify-center mb-4">
            {brand.logoUrl ? (
              <img
                src={brand.logoUrl}
                alt={`${brand.productNameVibe} logo`}
                className="w-14 h-14 rounded-full"
              />
            ) : (
              <Sparkles className="w-10 h-10 text-vibe-accent" />
            )}
          </div>
          <h1 className="text-3xl font-bold text-vibe-text">{brand.productNameVibe}</h1>
          <p className="mt-2 text-vibe-text-secondary">{brand.displayName} Platform</p>
        </div>

        {/* Login Section */}
        <div className="mt-8 space-y-4">
          <p className="text-center text-vibe-text-secondary">
            Sign in to access the {brand.productNameVibe} and build AI-powered applications.
          </p>

          <button
            onClick={handleLogin}
            className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-vibe-accent hover:bg-vibe-accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-vibe-accent transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            Sign in with Authentik
          </button>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-xs text-vibe-text-tertiary">
          <p>Protected by Authentik Single Sign-On</p>
          <p className="mt-1">{brand.displayName} {brand.productNameVibe} v1.0</p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
